import type { LandingPageContent } from '../types'

// GitLab-Issue #143: Übersetzung der deutschen How-to-Seite (so-funktioniert-es-fuer-kunden.ts),
// gleiche topicId für hreflang. Screenshots sind dieselben Bilddateien wie in der deutschen
// Version (Roofora läuft auch auf Spanisch/Polnisch, die Screenshots selbst stammen aber aus einer
// deutschsprachigen Test-Session - im Intro-Text transparent gemacht statt stillschweigend eine
// bereits übersetzte Oberfläche vorzutäuschen.
export const COMO_FUNCIONA_PARA_CLIENTES: LandingPageContent = {
  slug: 'como-funciona-para-clientes',
  lang: 'es',
  topicId: 'so-funktioniert-es-fuer-kunden',
  title: 'Cómo funciona Roofora para clientes – Guía paso a paso',
  metaDescription:
    'Guía paso a paso para clientes: crear una cuenta, publicar una solicitud con fotos del tejado ' +
    'y del cuadro eléctrico, comparar presupuestos y autorizar el contacto solo con el instalador ' +
    'que elija.',
  keywords: [
    'Roofora guía',
    'Roofora cómo funciona',
    'cómo solicitar presupuesto fotovoltaico',
    'cómo comparar presupuestos fotovoltaicos',
    'autorizar contacto instalador solar',
  ],
  eyebrow: 'Guía para clientes',
  h1: 'Cómo funciona Roofora para clientes',
  intro:
    'Esta guía muestra cada paso desde la perspectiva del cliente - desde el primer registro hasta ' +
    'la autorización de contacto a un instalador - con capturas de pantalla reales de la ' +
    'aplicación. Las capturas provienen de una sesión de prueba en alemán; la aplicación está ' +
    'disponible también en español, así que sus propios botones aparecerán en su idioma.',
  sections: [
    { type: 'heading', text: 'Explicado en 3 minutos' },
    {
      type: 'paragraph',
      text:
        'Crea una cuenta anónima, sube fotos de su tejado y de su cuadro eléctrico, y con eso ' +
        'publica una solicitud anonimizada. Instaladores de su zona presentan un presupuesto de ' +
        'forma independiente. Usted compara los precios y solo comparte sus datos de contacto ' +
        'cuando decide con qué instalador concreto quiere hablar.',
    },
    { type: 'heading', text: '¿Qué necesito antes de empezar?' },
    {
      type: 'list',
      items: [
        'Una a tres fotos de su tejado o vivienda (sirven fotos hechas con el móvil)',
        'Una foto de su cuadro eléctrico',
        'Su código postal',
        'Datos aproximados sobre vivienda, tejado y consumo eléctrico - muchos campos ya vienen con un valor sugerido',
        'No necesita correo electrónico ni teléfono, solo un nombre de usuario y una contraseña',
      ],
    },
  ],
  steps: [
    {
      title: 'Empezar desde la página principal',
      whatYouSee:
        'En la sección principal de roofora.de hay dos botones grandes - uno para clientes y otro ' +
        'para instaladores - y debajo una lista de argumentos de confianza como "cuenta sin correo ' +
        'ni teléfono".',
      whatToDo: 'Haga clic en el botón principal dirigido a clientes ("Obtener presupuestos gratis" o equivalente en su idioma).',
      whatHappensNext: 'Accederá a la página de registro para clientes, con las pestañas "Registrarse" e "Iniciar sesión".',
      successCriterion: 'Se muestra la pantalla de creación de cuenta con los campos de nombre de usuario y contraseña.',
      screenshot: {
        src: '/how-to/kunde/01-hero-cta.jpg',
        alt: 'Página principal de Roofora con los botones de acceso para clientes e instaladores',
        width: 1120,
        height: 531,
      },
    },
    {
      title: 'Registrar una cuenta anónima',
      whatYouSee:
        'Un formulario con los campos de nombre de usuario (con un enlace para generar uno al azar) ' +
        'y contraseña, además de un aviso de que tras el registro recibirá un código de recuperación.',
      whatToDo:
        'Genere un nombre de usuario o elija uno propio, defina una contraseña de al menos 8 ' +
        'caracteres y confirme la creación de la cuenta.',
      whatHappensNext: 'El sistema comprueba en tiempo real si el nombre de usuario está disponible.',
      successCriterion: 'Ambos campos están rellenados y el botón de crear cuenta está activo.',
      troubleshooting: 'Si el nombre de usuario ya existe, aparece un aviso inmediato - simplemente pruebe otro.',
      screenshot: {
        src: '/how-to/kunde/02-registrierung.jpg',
        alt: 'Formulario de registro relleno con nombre de usuario y contraseña, aviso de disponibilidad',
        width: 1120,
        height: 531,
      },
    },
    {
      title: 'Guardar el código de recuperación',
      whatYouSee:
        'Su nombre de usuario, contraseña (oculta) y un código de recuperación, con opciones para ' +
        'copiarlos o descargarlos como archivo de texto.',
      whatToDo:
        'Guarde al menos el código de recuperación en un lugar seguro, por ejemplo descargándolo, y ' +
        'confirme la casilla de que ha guardado ambos datos.',
      whatHappensNext: 'El botón para continuar a la plataforma solo se activa después de confirmar la casilla.',
      successCriterion: 'Accede a su panel de cliente.',
      troubleshooting:
        'No existe recuperación de contraseña por correo electrónico - sin el código de recuperación ' +
        'no podrá recuperar la cuenta si pierde la contraseña.',
      screenshot: {
        src: '/how-to/kunde/03-account-bereit.jpg',
        alt: 'Pantalla de confirmación con nombre de usuario, contraseña y código de recuperación',
        width: 1120,
        height: 531,
      },
    },
    {
      title: 'Panel de cliente',
      whatYouSee:
        'Su nombre de usuario, el identificador de cliente y tres bloques - "Mi perfil", "Mis ' +
        'solicitudes" y "Mis autorizaciones de contacto" - además del botón para crear una nueva solicitud.',
      whatToDo: 'Haga clic en el botón para crear una nueva solicitud y empezar su primera petición solar.',
      whatHappensNext: 'Se abre el asistente de 6 pasos para crear la solicitud.',
      successCriterion: 'Se muestran las pestañas del paso 1 al paso 6 en la parte superior.',
      screenshot: {
        src: '/how-to/kunde/04-dashboard.jpg',
        alt: 'Panel de cliente con los bloques Mi perfil, Mis solicitudes y Mis autorizaciones de contacto',
        width: 1120,
        height: 531,
      },
    },
    {
      title: 'Asistente: dirección y vivienda',
      whatYouSee:
        'Una vista previa en directo con una recomendación de instalación y su rentabilidad, y ' +
        'debajo campos sobre el tipo de encargo, país, código postal, tipo de vivienda, año de ' +
        'construcción, forma y material del tejado.',
      whatToDo:
        'Rellene los campos - muchos ya vienen con un valor razonable predefinido. El único campo ' +
        'obligatorio es el código postal.',
      whatHappensNext: 'La vista previa se actualiza automáticamente con cada dato introducido.',
      successCriterion: 'Todos los campos obligatorios están completos y el botón "Siguiente" lleva al paso 2.',
      troubleshooting: 'Sin código postal no se puede avanzar - el propio formulario lo indica.',
      screenshot: {
        src: '/how-to/kunde/05-wizard-start.jpg',
        alt: 'Paso "Dirección y vivienda" del asistente con vista previa y selección de tipo de encargo',
        width: 1120,
        height: 531,
      },
    },
    {
      title: 'Subir fotos del tejado',
      whatYouSee:
        'Para cada cara del tejado, campos de orientación, superficie e inclinación, y debajo el ' +
        'área para subir fotos del tejado o la vivienda con un contador "0/3 fotos".',
      whatToDo: 'Suba entre una y tres fotos de su tejado o vivienda - sirven fotos hechas con el móvil.',
      whatHappensNext: 'Las fotos subidas aparecen como miniatura con una opción para eliminarlas, y el contador sube a "1/3 fotos".',
      successCriterion: 'Hay al menos una foto del tejado subida.',
      troubleshooting: 'Sin al menos una foto no podrá publicar la solicitud más adelante.',
      screenshot: {
        src: '/how-to/kunde/06-dachfotos.jpg',
        alt: 'Área de subida de fotos del tejado con una foto cargada, contador 1/3 fotos',
        width: 1120,
        height: 567,
      },
    },
    {
      title: 'Añadir la foto del cuadro eléctrico',
      whatYouSee:
        'En el paso 3 del asistente, el área para subir fotos del cuadro eléctrico, junto con ' +
        'preguntas sobre el cableado y dónde instalar el inversor o la batería.',
      whatToDo:
        'Suba al menos una foto de su cuadro eléctrico y responda a las preguntas técnicas según su ' +
        'conocimiento - "no lo sé" es siempre una respuesta válida.',
      whatHappensNext: 'El contador de fotos pasa a "1/2 fotos".',
      successCriterion: 'Hay al menos una foto del cuadro eléctrico subida.',
      troubleshooting: 'Esta foto también es obligatoria para poder continuar.',
      screenshot: {
        src: '/how-to/kunde/07-zaehlerschrank-foto.jpg',
        alt: 'Área de subida de fotos del cuadro eléctrico con una foto cargada',
        width: 1120,
        height: 567,
      },
    },
    {
      title: 'Definir preferencia de contacto y franja horaria',
      whatYouSee:
        'En el último paso del asistente, la opción de permitir o no que le contacten, y debajo ' +
        'varias franjas horarias disponibles.',
      whatToDo:
        'Elija "sí" si desea que los instaladores puedan contactarle tras su autorización individual, ' +
        'y marque su franja horaria preferida.',
      whatHappensNext: 'Esta información solo se comparte con los instaladores que usted autorice más adelante, no con todos automáticamente.',
      successCriterion: 'Hay una franja horaria marcada.',
      screenshot: {
        src: '/how-to/kunde/08-kontakt-zeitfenster.jpg',
        alt: 'Selección de preferencia de contacto y franja horaria en el asistente',
        width: 1120,
        height: 567,
      },
    },
    {
      title: 'Publicar la solicitud',
      whatYouSee:
        'Un resumen final con la recomendación de instalación, la rentabilidad y una simulación de ' +
        'financiación, y debajo los botones "Guardar borrador" y "Publicar".',
      whatToDo: 'Revise el resumen y confirme la publicación.',
      whatHappensNext:
        'Su solicitud queda visible de forma anonimizada para los instaladores de su zona, sin sus ' +
        'datos de contacto.',
      successCriterion: 'Aparece el estado en verde "Publicada" y el plazo para recibir presupuestos.',
      troubleshooting: '¿Aún no ha terminado? Guarde un borrador para completar la solicitud más tarde sin publicarla todavía.',
      screenshot: {
        src: '/how-to/kunde/09-veroeffentlicht.jpg',
        alt: 'Solicitud publicada con el estado "Publicada" y plazo de presupuestos',
        width: 1120,
        height: 567,
      },
    },
    {
      title: 'Comparar presupuestos y preparar el contacto',
      whatYouSee:
        'La lista de presupuestos recibidos ordenados de menor a mayor precio, con el nombre de la ' +
        'empresa, el precio y la etiqueta del mejor presupuesto, además de un botón para solicitar ' +
        'el contacto gratuito y una explicación de que sus datos solo se comparten tras su aprobación.',
      whatToDo: 'Compare los precios y, en la empresa que quiera contactar, pulse el botón de solicitar contacto.',
      whatHappensNext:
        'Aparece un diálogo de confirmación - a partir de ese momento el encargo deja de mostrarse a ' +
        'otros instaladores, aunque todavía no supone ningún compromiso contractual.',
      successCriterion: 'Ha seleccionado la empresa deseada y se muestra el aviso sobre la protección de sus datos.',
      troubleshooting: 'Si ningún presupuesto le convence, puede rechazarlo en lugar de solicitar el contacto.',
      screenshot: {
        src: '/how-to/kunde/10-angebot-kontakt-erklaerung.jpg',
        alt: 'Presupuesto con precio, botón para solicitar contacto gratuito y explicación de privacidad',
        width: 1120,
        height: 567,
      },
    },
    {
      title: 'Completar los datos de contacto',
      whatYouSee:
        'Un cuadro de diálogo para completar datos de contacto, con correo electrónico y teléfono ' +
        'como campos obligatorios (basta con uno de los dos) y campos opcionales para nombre y dirección.',
      whatToDo: 'Indique al menos una forma de contacto y confirme para guardar y autorizar.',
      whatHappensNext: 'Solo la empresa seleccionada verá exactamente los datos que rellene aquí, sin compartirlos con nadie más.',
      successCriterion: 'El diálogo se cierra y la autorización queda completada.',
      troubleshooting: 'Sin al menos un correo o un teléfono no puede finalizar este paso.',
      screenshot: {
        src: '/how-to/kunde/11-kontaktdaten-dialog.jpg',
        alt: 'Diálogo para completar datos de contacto con campos de correo electrónico y teléfono',
        width: 1120,
        height: 567,
      },
    },
    {
      title: 'Contacto autorizado correctamente',
      whatYouSee: 'Junto al presupuesto aparece el mensaje verde de que la empresa ya puede contactarle, además de la opción de exportar el presupuesto en PDF.',
      whatToDo: 'No hace falta nada más - el instalador se pondrá en contacto con usted en la franja horaria elegida.',
      whatHappensNext: 'El encargo deja de ser visible para otras empresas; en "Mis autorizaciones de contacto" puede ver todos sus contactos autorizados.',
      successCriterion: 'Se muestra el mensaje verde de confirmación.',
      screenshot: {
        src: '/how-to/kunde/12-freigegeben-erfolg.jpg',
        alt: 'Mensaje de éxito indicando que la empresa ya puede contactar al cliente',
        width: 1120,
        height: 567,
      },
    },
  ],
  faq: [
    {
      q: '¿Necesito un correo electrónico para usar Roofora?',
      a:
        'No. El registro funciona completamente de forma anónima con nombre de usuario y contraseña. ' +
        'Sin correo electrónico no podemos avisarle activamente de nuevos presupuestos o solicitudes ' +
        'de contacto - revise su cuenta con regularidad, o añada un correo de forma opcional.',
    },
    {
      q: '¿Usar Roofora como cliente tiene algún coste?',
      a: 'No, crear una solicitud, recibir presupuestos y autorizar el contacto es gratuito para clientes.',
    },
    {
      q: '¿Tengo que elegir el presupuesto más barato?',
      a: 'No. Usted decide libremente si contacta con algún instalador y con cuál - autorizar el contacto no implica ningún contrato.',
    },
    {
      q: '¿Qué pasa si ningún instalador presenta presupuesto?',
      a: 'Su solicitud permanece visible hasta que finalice el plazo de presupuestos. Puede editarla en cualquier momento.',
    },
    {
      q: '¿A cuántos instaladores puedo dar mis datos de contacto?',
      a: 'Usted autoriza a cada empresa individualmente - un límite por solicitud evita que demasiadas empresas le contacten a la vez.',
    },
  ],
  primaryCta: { label: 'Solicitar presupuesto gratis', action: 'customer' },
  secondaryCta: { label: 'Ver antes los costes estimados', action: 'link', href: '/coste-instalacion-fotovoltaica' },
  relatedLinks: [
    { label: 'Solicitar presupuesto fotovoltaico', href: '/solicitar-presupuesto-fotovoltaico' },
    { label: 'Cómo funciona la comparación de presupuestos', href: '/comparar-presupuestos-fotovoltaicos' },
    { label: 'Presupuesto sin necesidad de teléfono', href: '/presupuesto-fotovoltaico-sin-telefono' },
    { label: 'Cómo funciona Roofora para instaladores', href: '/como-funciona-para-instaladores' },
  ],
}
